// ─── Firebase Configuration ───────────────────────────────────────────────────
export const FIREBASE_CONFIG = {
  apiKey: "AIzaSyAz6F2p5HL2yVqVAken_ruteNVfAhOfV1w",
  authDomain: "three9-crm.firebaseapp.com",
  projectId: "three9-crm",
  storageBucket: "three9-crm.firebasestorage.app",
  messagingSenderId: "492331376534",
  appId: "1:492331376534:web:ad408e416a4142760b07b7",
  measurementId: "G-T91XK261ZJ"
};

export const FIREBASE_ENABLED = !!(
  FIREBASE_CONFIG.apiKey && FIREBASE_CONFIG.apiKey.length > 10 &&
  FIREBASE_CONFIG.projectId && FIREBASE_CONFIG.projectId.length > 2
);

let _db = null;
let _auth = null;
let _initialized = false;

export function getDB() { return _db; }
export function getFBAuth() { return _auth; }

export async function initFirebase() {
  if (_initialized) return true;
  if (!FIREBASE_ENABLED) return false;
  try {
    const { initializeApp, getApps, getApp } = await import('firebase/app');
    const { getFirestore } = await import('firebase/firestore');
    const { getAuth: fbGetAuth } = await import('firebase/auth');
    const app = getApps().length ? getApp() : initializeApp(FIREBASE_CONFIG);
    _db = getFirestore(app);
    _auth = fbGetAuth(app);
    _initialized = true;
    return true;
  } catch (e) {
    console.error('Firebase init error:', e);
    return false;
  }
}

export async function loginFirebase(email, password) {
  const { signInWithEmailAndPassword } = await import('firebase/auth');
  return signInWithEmailAndPassword(_auth, email, password);
}
export async function logoutFirebase() {
  const { signOut } = await import('firebase/auth');
  return signOut(_auth);
}
export async function sendPasswordReset(email) {
  const { sendPasswordResetEmail } = await import('firebase/auth');
  return sendPasswordResetEmail(_auth, email);
}
let _secondaryApp = null;
export async function createFirebaseUser(email, password) {
  // Firebase's normal createUserWithEmailAndPassword immediately signs the
  // CALLING browser in as the newly created user — which means an admin
  // creating a client or employee login would suddenly be logged in as that
  // new account instead of themselves. A secondary, separately-authenticated
  // app instance avoids that: the new account is created there, and that
  // instance is discarded — the admin's own primary session is never touched.
  const { initializeApp, getApps, deleteApp } = await import('firebase/app');
  const { getAuth: fbGetAuth, createUserWithEmailAndPassword, signOut: secondarySignOut } = await import('firebase/auth');
  const name = 'AccountCreator';
  const existing = getApps().find(a => a.name === name);
  if (existing) { try { await deleteApp(existing); } catch {} }
  const secondaryApp = initializeApp(FIREBASE_CONFIG, name);
  const secondaryAuth = fbGetAuth(secondaryApp);
  try {
    const cred = await createUserWithEmailAndPassword(secondaryAuth, email, password);
    const uid = cred.user.uid;
    try { await secondarySignOut(secondaryAuth); } catch {}
    try { await deleteApp(secondaryApp); } catch {}
    return { user: { uid } };
  } catch (e) {
    try { await deleteApp(secondaryApp); } catch {}
    throw e;
  }
}
export async function fbSaveDoc(col, id, data) {
  const { doc, setDoc } = await import('firebase/firestore');
  // merge:true is essential — without it, any partial-object save (like the
  // candidate photo-upload link, which only sends {id, photo}) would silently
  // wipe out every other field on the document instead of just updating photo.
  return setDoc(doc(_db, col, id), { ...data, _updatedAt: new Date().toISOString() }, { merge: true });
}
export async function fbGetDoc(col, id) {
  const { doc, getDoc } = await import('firebase/firestore');
  const snap = await getDoc(doc(_db, col, id));
  return snap.exists() ? snap.data() : null;
}
export async function fbGetAll(col) {
  const { collection, getDocs } = await import('firebase/firestore');
  const snap = await getDocs(collection(_db, col));
  return snap.docs.map(d => d.data());
}
// Realtime listener — keeps the app in sync in the background across devices/tabs.
// Returns an unsubscribe function.
export async function fbListenAll(col, callback) {
  const { collection, onSnapshot } = await import('firebase/firestore');
  return onSnapshot(collection(_db, col), snap => {
    callback(snap.docs.map(d => d.data()));
  }, err => console.error(`Realtime sync error (${col}):`, err));
}
export async function fbListenDoc(col, id, callback) {
  const { doc, onSnapshot } = await import('firebase/firestore');
  return onSnapshot(doc(_db, col, id), snap => {
    callback(snap.exists() ? snap.data() : null);
  }, err => console.error(`Realtime sync error (${col}/${id}):`, err));
}
export async function fbDelete(col, id) {
  const { doc, deleteDoc } = await import('firebase/firestore');
  return deleteDoc(doc(_db, col, id));
}
export async function fbSetDoc(col, id, data) {
  return fbSaveDoc(col, id, data);
}

// Realtime listener on a filtered query (e.g. one WhatsApp conversation's messages).
export async function fbListenWhere(col, field, value, callback) {
  const { collection, query, where, onSnapshot } = await import('firebase/firestore');
  return onSnapshot(query(collection(_db, col), where(field, '==', value)), snap => {
    callback(snap.docs.map(d => d.data()));
  }, err => console.error(`Realtime sync error (${col} where ${field}):`, err));
}
// Calls a Firebase Cloud Function (callable) as the signed-in user.
export async function fbCallFunction(name, data) {
  const { getFunctions, httpsCallable } = await import('firebase/functions');
  const { getApp } = await import('firebase/app');
  const fn = httpsCallable(getFunctions(getApp()), name);
  const res = await fn(data);
  return res.data;
}
